/**
 * Where the template puts its embedded chart, and how big.
 *
 * Univer cannot render an embedded Excel chart, so the SDMS view reconstructs the
 * calibration curve with Recharts and floats it over the grid. Its box used to be
 * hardcoded CSS (`top:250px; left:46px; width:480px; height:250px`), which is why
 * the chart came out SMALLER than ALPACA's on QB26052810_032 (client 2026-08-24)
 * — a fixed box cannot match a chart whose real size comes from the cells it spans.
 *
 * Excel stores the position as a `twoCellAnchor`: a FROM cell + offset and a TO
 * cell + offset. The rendered size is therefore the total width of the spanned
 * columns and the total height of the spanned rows — it changes with the sheet's
 * own column widths and row heights, and differs per template. Reading the anchor
 * makes the float track that, so every template gets its chart at the size and
 * place its own designer chose.
 *
 * ExcelJS does not expose charts at all, so the anchor is parsed straight out of
 * the .xlsx (it is a zip): sheet -> its .rels -> the drawing part -> the anchor.
 * Offsets are EMU (English Metric Units), 914400 per inch, so at 96 dpi one pixel
 * is 9525 EMU.
 */
import JSZip from 'jszip';

const EMU_PER_PX = 9525;

export interface ChartAnchor {
  /** 0-based index of the worksheet this drawing belongs to. */
  sheetIndex: number;
  /** 0-based column/row of the FROM corner, plus its offset INTO that cell (px). */
  fromCol: number; fromColOffPx: number;
  fromRow: number; fromRowOffPx: number;
  /** 0-based column/row of the TO corner, plus its offset into that cell (px). */
  toCol: number; toColOffPx: number;
  toRow: number; toRowOffPx: number;
}

const num = (xml: string, tag: string): number => {
  const m = xml.match(new RegExp(`<xdr:${tag}>\\s*(-?\\d+)\\s*</xdr:${tag}>`));
  return m ? Number(m[1]) : 0;
};

/**
 * Every chart/picture `twoCellAnchor` in the workbook, tagged with the sheet it
 * sits on. Returns an empty array for a workbook with no drawings, and never
 * throws — a malformed drawing part must not cost the sheet its rendering.
 */
export async function readChartAnchors(buf: ArrayBuffer): Promise<ChartAnchor[]> {
  const out: ChartAnchor[] = [];
  try {
    const zip = await JSZip.loadAsync(buf);

    // Sheet order as the workbook declares it, so sheetIndex matches ExcelJS's.
    const wbXml = await zip.file('xl/workbook.xml')?.async('string');
    if (!wbXml) return out;
    const sheetIds = [...wbXml.matchAll(/<sheet\b[^>]*r:id="([^"]+)"/g)].map(m => m[1]);
    const wbRels = (await zip.file('xl/_rels/workbook.xml.rels')?.async('string')) ?? '';
    const relTarget = (rels: string, id: string): string | null => {
      const m = rels.match(new RegExp(`<Relationship\\b[^>]*Id="${id}"[^>]*Target="([^"]+)"`))
        ?? rels.match(new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${id}"`));
      return m?.[1] ?? null;
    };

    for (let i = 0; i < sheetIds.length; i++) {
      const target = relTarget(wbRels, sheetIds[i]!);
      if (!target) continue;
      const sheetPath = ('xl/' + target).replace(/^xl\/\/+/, 'xl/').replace(/\/\.\//g, '/');
      const sheetXml = await zip.file(sheetPath)?.async('string');
      if (!sheetXml) continue;
      const drawingId = sheetXml.match(/<drawing\b[^>]*r:id="([^"]+)"/)?.[1];
      if (!drawingId) continue;                                  // no drawing on this sheet

      const base = sheetPath.replace(/\/[^/]+$/, '');            // e.g. xl/worksheets
      const file = sheetPath.replace(/^.*\//, '');
      const sheetRels = (await zip.file(`${base}/_rels/${file}.rels`)?.async('string')) ?? '';
      const dTarget = relTarget(sheetRels, drawingId);
      if (!dTarget) continue;
      // Targets are relative to the sheet part, typically "../drawings/drawing1.xml".
      const drawingPath = new URL(dTarget, 'file:///' + base + '/').pathname.replace(/^\//, '');
      const dXml = await zip.file(drawingPath)?.async('string');
      if (!dXml) continue;

      for (const m of dXml.matchAll(/<xdr:twoCellAnchor[\s\S]*?<\/xdr:twoCellAnchor>/g)) {
        const block = m[0];
        const from = block.match(/<xdr:from>([\s\S]*?)<\/xdr:from>/)?.[1] ?? '';
        const to   = block.match(/<xdr:to>([\s\S]*?)<\/xdr:to>/)?.[1] ?? '';
        if (!from || !to) continue;
        out.push({
          sheetIndex: i,
          fromCol: num(from, 'col'), fromColOffPx: num(from, 'colOff') / EMU_PER_PX,
          fromRow: num(from, 'row'), fromRowOffPx: num(from, 'rowOff') / EMU_PER_PX,
          toCol:   num(to,   'col'), toColOffPx:   num(to,   'colOff') / EMU_PER_PX,
          toRow:   num(to,   'row'), toRowOffPx:   num(to,   'rowOff') / EMU_PER_PX,
        });
      }
    }
  } catch {
    return out;                     // unreadable drawing -> caller keeps its default box
  }
  return out;
}

/** A cell range a chart series points at, resolved from its `Sheet!$A$3:$A$9` reference. */
export interface SeriesRef { sheet: string; startRow: number; startCol: number; endRow: number; endCol: number }

/** What the template's embedded chart actually plots, per sheet. */
export interface ChartSeries {
  /** Category (X) values, from `<c:cat>`. */
  x: SeriesRef | null;
  /** Series (Y) values, from `<c:val>`. */
  y: SeriesRef | null;
  /** The value axis title, e.g. "Absorbance" or "Reading". */
  yTitle: string | null;
  /** Whether the chart carries a `<c:legend>` at all. Templates differ: Phosphate has one
   *  on the right, Color has none — and LIMS renders each accordingly. */
  legend: boolean;
  /** Whether the series draws point markers. `<c:symbol val="none"/>` means no; NO marker
   *  element at all means Excel's default (a diamond), which is what Color shows. */
  markers: boolean;
}

const A1_COL = (s: string): number => {
  let v = 0;
  for (const ch of s) v = v * 26 + (ch.charCodeAt(0) - 64);
  return v - 1;                                         // 0-based
};

/** `Calibration!$A$3:$A$9` (or `'Raw Data'!$A$3:$B$9`) → a 0-based range. */
export function parseSeriesRef(ref: string): SeriesRef | null {
  const m = ref.match(/^(?:'([^']+)'|([^!]+))!\$?([A-Z]+)\$?(\d+)(?::\$?([A-Z]+)\$?(\d+))?$/);
  if (!m) return null;
  const sheet = m[1] ?? m[2] ?? '';
  const c1 = A1_COL(m[3]!), r1 = Number(m[4]) - 1;
  const c2 = m[5] ? A1_COL(m[5]) : c1;
  const r2 = m[6] ? Number(m[6]) - 1 : r1;
  return { sheet, startRow: Math.min(r1, r2), startCol: Math.min(c1, c2), endRow: Math.max(r1, r2), endCol: Math.max(c1, c2) };
}

/**
 * What each sheet's embedded chart plots, read from the chart part itself.
 *
 * The SDMS calibration chart is reconstructed with Recharts (Univer cannot render an
 * embedded Excel chart), and the reconstruction used to find its data by looking for
 * columns literally headed "Known Conc" and "A". Templates that head them differently —
 * Color/8084 uses SpecifiedConc and CalculatedConc — matched nothing and drew an EMPTY
 * BOX (client 2026-08-24, QB26082010_074). The chart already states its own series:
 *     <c:cat><c:f>Calibration!$A$3:$A$9</c:f>   <c:val><c:f>Calibration!$B$3:$B$9</c:f>
 * Reading that plots whatever the template plots, whatever its columns are called.
 *
 * Never throws: an unreadable chart returns nothing and the caller keeps its old
 * header-matching path.
 */
export async function readChartSeries(buf: ArrayBuffer): Promise<Map<string, ChartSeries>> {
  const out = new Map<string, ChartSeries>();
  try {
    const zip = await JSZip.loadAsync(buf);
    const wbXml = await zip.file('xl/workbook.xml')?.async('string');
    if (!wbXml) return out;
    const sheets = [...wbXml.matchAll(/<sheet\b[^>]*?name="([^"]*)"[^>]*?r:id="([^"]+)"/g)]
      .map(m => ({ name: m[1]!, rid: m[2]! }));
    const wbRels = (await zip.file('xl/_rels/workbook.xml.rels')?.async('string')) ?? '';
    const target = (rels: string, id: string): string | null => {
      const m = rels.match(new RegExp(`<Relationship\\b[^>]*Id="${id}"[^>]*Target="([^"]+)"`))
        ?? rels.match(new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${id}"`));
      return m?.[1] ?? null;
    };
    const resolve = (base: string, rel: string) =>
      new URL(rel, 'file:///' + base + '/').pathname.replace(/^\//, '');

    for (const { name, rid } of sheets) {
      const sheetTarget = target(wbRels, rid);
      if (!sheetTarget) continue;
      const sheetPath = ('xl/' + sheetTarget).replace(/^xl\/\/+/, 'xl/');
      const sheetXml = await zip.file(sheetPath)?.async('string');
      const drawingId = sheetXml?.match(/<drawing\b[^>]*r:id="([^"]+)"/)?.[1];
      if (!drawingId) continue;
      const sBase = sheetPath.replace(/\/[^/]+$/, ''), sFile = sheetPath.replace(/^.*\//, '');
      const sRels = (await zip.file(`${sBase}/_rels/${sFile}.rels`)?.async('string')) ?? '';
      const dTarget = target(sRels, drawingId);
      if (!dTarget) continue;
      const drawingPath = resolve(sBase, dTarget);
      const dXml = await zip.file(drawingPath)?.async('string');
      // The graphicFrame names the chart part through the DRAWING's own rels.
      const chartId = dXml?.match(/<c:chart\b[^>]*r:id="([^"]+)"/)?.[1];
      if (!chartId) continue;
      const dBase = drawingPath.replace(/\/[^/]+$/, ''), dFile = drawingPath.replace(/^.*\//, '');
      const dRels = (await zip.file(`${dBase}/_rels/${dFile}.rels`)?.async('string')) ?? '';
      const cTarget = target(dRels, chartId);
      if (!cTarget) continue;
      const chartXml = await zip.file(resolve(dBase, cTarget))?.async('string');
      if (!chartXml) continue;

      const refIn = (tag: string): SeriesRef | null => {
        const block = chartXml.match(new RegExp(`<c:${tag}>([\\s\\S]*?)</c:${tag}>`))?.[1];
        const f = block?.match(/<c:f>([\s\S]*?)<\/c:f>/)?.[1];
        return f ? parseSeriesRef(f.trim()) : null;
      };
      // Fall back to the series' two <c:f> in order (cat, then val) if the wrappers differ.
      const allF = [...chartXml.matchAll(/<c:f>([\s\S]*?)<\/c:f>/g)].map(m => m[1]!.trim());
      const x = refIn('cat') ?? (allF[0] ? parseSeriesRef(allF[0]) : null);
      const y = refIn('val') ?? (allF[1] ? parseSeriesRef(allF[1]) : null);
      // The value axis carries the title ("Absorbance", "Reading").
      const valAx = [...chartXml.matchAll(/<c:valAx>([\s\S]*?)<\/c:valAx>/g)].map(m => m[1]!);
      let yTitle: string | null = null;
      for (const ax of valAx) {
        const t = ax.match(/<c:title>[\s\S]*?<a:t>([\s\S]*?)<\/a:t>/)?.[1];
        if (t) { yTitle = t.trim(); break; }
      }
      const legend = /<c:legend[\s>]/.test(chartXml);
      // No <c:marker> element = Excel's default marker. `<c:symbol val="none"/>` = none.
      const markers = !/<c:symbol\b[^>]*val="none"/.test(chartXml);
      if (x || y) out.set(name, { x, y, yTitle, legend, markers });
    }
  } catch {
    return out;
  }
  return out;
}

export interface AnchorBox { left: number; top: number; width: number; height: number }

/**
 * The anchor as a pixel box, given the rendered size of each column and row.
 *
 * `colWidthPx(c)` / `rowHeightPx(r)` take 0-based indices and must return what the
 * GRID actually renders — pass Univer's own widths/heights, not Excel's, or the box
 * will be right in shape and wrong in scale. `originLeft` / `originTop` are the
 * grid's header offsets (the row-number gutter and the column-letter strip).
 */
export function anchorToBox(
  a: ChartAnchor,
  colWidthPx: (c: number) => number,
  rowHeightPx: (r: number) => number,
  originLeft = 0,
  originTop = 0,
): AnchorBox {
  let left = originLeft;
  for (let c = 0; c < a.fromCol; c++) left += colWidthPx(c);
  left += a.fromColOffPx;

  let top = originTop;
  for (let r = 0; r < a.fromRow; r++) top += rowHeightPx(r);
  top += a.fromRowOffPx;

  // Width spans the REMAINDER of the from-column, the whole columns between, and
  // the leading part of the to-column — which is exactly (sum of columns from..to)
  // minus the leading offset, plus the trailing offset.
  let width = -a.fromColOffPx + a.toColOffPx;
  for (let c = a.fromCol; c < a.toCol; c++) width += colWidthPx(c);

  let height = -a.fromRowOffPx + a.toRowOffPx;
  for (let r = a.fromRow; r < a.toRow; r++) height += rowHeightPx(r);

  return { left, top, width: Math.max(0, width), height: Math.max(0, height) };
}
